import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, Legend, AreaChart, Area
} from 'recharts';
import { ArrowUp, ArrowDown, Users, Eye, Activity, RefreshCw, Clock } from 'lucide-react';
import api from '../api/client';
import { message, Spin, Select } from 'antd';

const { Option } = Select;

const KPICard = ({ title, value, subValue, icon: Icon, trend }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-surface p-6 rounded-xl border border-white/5 relative overflow-hidden group"
  >
    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
      <Icon size={48} />
    </div>
    <div className="flex items-center gap-3 mb-2">
      <div className="p-2 bg-primary/20 rounded-lg text-primary">
        <Icon size={20} />
      </div>
      <span className="text-slate-400 text-sm font-medium">{title}</span>
    </div>
    <div className="text-3xl font-bold text-white mb-1">{value}</div>
    {subValue && (
      <div className={`text-xs font-medium flex items-center gap-1 ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-rose-400' : 'text-slate-500'}`}>
        {trend === 'up' ? <ArrowUp size={12} /> : trend === 'down' ? <ArrowDown size={12} /> : null}
        {subValue}
      </div>
    )}
  </motion.div>
);

const ChartCard = ({ title, children }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-surface p-6 rounded-xl border border-white/5 h-[400px] flex flex-col"
  >
    <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
      {title}
    </h3>
    <div className="flex-1 w-full min-h-0">
      {children}
    </div>
  </motion.div>
);

export default function ChannelAnalytics() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [data, setData] = useState(null);

  const subscribers = Number(data?.summary?.subscribers || 0);
  const avgViews = Number(data?.summary?.avg_views || 0);
  const reachPercent = subscribers > 0 ? ((avgViews / subscribers) * 100) : 0;

  const history = Array.isArray(data?.history) ? data.history : [];
  const last = history.length > 0 ? history[history.length - 1] : null;
  const prev = history.length > 1 ? history[history.length - 2] : null;
  const subsDelta = last && prev ? Number(last.subscribers || 0) - Number(prev.subscribers || 0) : null;
  const subsTrend = subsDelta == null ? null : (subsDelta > 0 ? 'up' : subsDelta < 0 ? 'down' : null);
  const views24h = last ? Number(last.views_24h || 0) : 0;
  const posts24h = last ? Number(last.posts_24h || 0) : 0;
  const err24h = last && last.err_24h != null ? Number(last.err_24h) : null;

  useEffect(() => {
    fetchChannels();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchAnalytics(selectedChannel);
    }
  }, [selectedChannel]);

  const fetchChannels = async () => {
    try {
      const res = await api.get('/channels');
      setChannels(res.data);
      if (res.data.length > 0) {
        setSelectedChannel(res.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      message.error('Ошибка загрузки каналов');
      setLoading(false);
    }
  };

  const fetchAnalytics = async (channelId) => {
    try {
      setLoading(true);
      const res = await api.get(`/channel-analytics/${channelId}`);
      setData(res.data);
    } catch (err) {
      message.error('Ошибка загрузки аналитики');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedChannel) return;
    try {
      setRefreshing(true);
      await api.post(`/channel-analytics/${selectedChannel}/refresh`);
      await fetchAnalytics(selectedChannel);
      message.success('Данные обновлены');
    } catch (err) {
      message.error('Ошибка обновления');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!selectedChannel) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl text-slate-400">Нет подключенных каналов</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Аналитика канала</h1>
          <div className="flex items-center gap-4">
            <Select
              value={selectedChannel}
              onChange={setSelectedChannel}
              className="w-64"
              dropdownStyle={{ backgroundColor: '#1e293b', borderColor: '#334155' }}
            >
              {channels.map(ch => (
                <Option key={ch.id} value={ch.id}>{ch.channel_name || ch.channel_username}</Option>
              ))}
            </Select>
            <span className="text-sm text-slate-400">
              Обновлено: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Обновление...' : 'Обновить данные'}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard 
          title="Подписчики" 
          value={subscribers ? subscribers.toLocaleString() : '—'} 
          subValue={subsDelta == null ? null : `${subsDelta > 0 ? '+' : ''}${subsDelta} за день`}
          trend={subsTrend}
          icon={Users} 
        />
        <KPICard 
          title="Охват за 24ч"
          value={views24h ? views24h.toLocaleString() : '—'}
          subValue={posts24h ? `${posts24h} постов` : null}
          icon={Eye}
        />
        <KPICard
          title="Средний охват" 
          value={avgViews ? avgViews.toLocaleString() : '—'} 
          subValue={subscribers ? `${reachPercent.toFixed(1)}% от базы` : null}
          icon={Eye} 
        />
        <KPICard 
          title="ERR за 24ч"
          value={err24h != null ? `${err24h.toFixed(2)}%` : '—'}
          subValue={data?.summary?.err != null ? `Среднее: ${data.summary.err}%` : null}
          icon={Activity} 
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Динамика подписчиков">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.history || []}>
              <defs>
                <linearGradient id="colorSubs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis 
                dataKey="date" 
                stroke="#94a3b8" 
                tick={{ fill: '#94a3b8' }} 
                tickLine={false}
              />
              <YAxis 
                stroke="#94a3b8" 
                tick={{ fill: '#94a3b8' }} 
                tickLine={false}
                domain={['auto', 'auto']}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                itemStyle={{ color: '#10b981' }}
              />
              <Area 
                type="monotone" 
                dataKey="subscribers" 
                stroke="#10b981" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorSubs)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Просмотры постов">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.posts_distribution?.views || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis 
                dataKey="id" 
                stroke="#94a3b8" 
                tick={false}
                label={{ value: 'Последние 50 постов', position: 'insideBottom', fill: '#64748b' }}
              />
              <YAxis 
                stroke="#94a3b8" 
                tick={{ fill: '#94a3b8' }} 
                tickLine={false}
              />
              <Tooltip 
                cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
              />
              <Bar dataKey="views" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Активность по часам (24ч)">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.activity || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis 
                dataKey="hour" 
                stroke="#94a3b8" 
                tick={{ fill: '#94a3b8' }} 
                interval={2}
              />
              <YAxis 
                stroke="#94a3b8" 
                tick={{ fill: '#94a3b8' }} 
                tickLine={false}
              />
              <Tooltip 
                cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
              />
              <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Посты" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="bg-surface p-6 rounded-xl border border-white/5 h-[400px] flex flex-col">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                Сводка вовлеченности
            </h3>
            <div className="flex-1 flex flex-col justify-center gap-6">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-slate-400">Реакции</span>
                    <span className="text-xl font-bold text-emerald-400">{data?.posts_distribution?.reactions || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-slate-400">Репосты</span>
                    <span className="text-xl font-bold text-blue-400">{data?.posts_distribution?.forwards || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-slate-400">Комментарии</span>
                    <span className="text-xl font-bold text-purple-400">0</span>
                </div>
                <div className="mt-4 text-center text-sm text-slate-500">
                    Данные на основе последних 50 постов
                </div>
            </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-surface p-6 rounded-xl border border-white/5"
      >
        <h3 className="text-lg font-semibold text-white mb-6">Топ постов</h3>

        <div className="space-y-3">
          {(data?.top_posts || []).slice(0, 5).map((p, idx) => (
            <div key={p.id} className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <div className="w-8 text-slate-500 font-mono">#{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{p.content_preview || 'Медиа контент'}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {p.posted_at ? new Date(p.posted_at).toLocaleString() : ''}
                </div>
              </div>
              <div className="text-right">
                <div className="text-slate-300"><span className="text-slate-500">Просмотры:</span> {p.views ?? 0}</div>
                <div className="text-slate-300"><span className="text-slate-500">Реакции:</span> {p.reactions ?? 0}</div>
                <div className="text-emerald-400 font-medium"><span className="text-slate-500">ER:</span> {p.er ?? 0}%</div>
              </div>
            </div>
          ))}

          {(!data?.top_posts || data.top_posts.length === 0) && (
            <div className="text-slate-500 text-sm">
              Пока нет данных по постам. Бот начнет собирать статистику для новых постов автоматически.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
